import { Grid, Typography } from "@mui/material";
import { debitNumberRange, debitNumberRangesOverlap } from "misc/debitNumberRangeHelpers";
import { BooleanInput, NumberInput, required, SelectInput, useTranslate } from "react-admin";

const AVAILABILITY_KEYS = [
  "order_validation_capacity",
  "order_validation_registration_window",
  "order_validation_event_started",
];
const VALIDATION_LEVELS = ["off", "internal", "everywhere"];

type DebitNumberValues = {
  debit_number_start_index?: number | null;
  debit_number_end_index?: number | null;
  organization_debit_number_start_index?: number | null;
  organization_debit_number_end_index?: number | null;
};

/**
 * Order and invoicing settings: whether order items are processed by hand, which
 * availability checks are enforced, how long a pending checkout holds seats, invoicing,
 * debit number ranges, and how long guest records are kept.
 */
export default function OrderFields() {
  const translate = useTranslate();

  const validateEnd =
    (startKey: keyof DebitNumberValues) => (value: number | null | undefined, values: DebitNumberValues) => {
      const start = values[startKey];
      if (value == null) return undefined;
      if (start == null) return translate("settings.debit_number_ranges.errors.end_without_start");
      if (value < start) return translate("settings.debit_number_ranges.errors.end_before_start");
      return undefined;
    };

  const validateOrganizationStart = (value: number | null | undefined, values: DebitNumberValues) => {
    const defaultRange = debitNumberRange(values.debit_number_start_index, values.debit_number_end_index);
    const organizationRange = debitNumberRange(value, values.organization_debit_number_end_index);
    if (defaultRange && organizationRange && debitNumberRangesOverlap(defaultRange, organizationRange)) {
      return translate("settings.debit_number_ranges.errors.overlap");
    }
    return undefined;
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h6">{translate("settings.headlines.order_processing")}</Typography>
      </Grid>
      <Grid item xs={12}>
        <BooleanInput
          source="order_manual_processing"
          label={translate("settings.order_manual_processing.name")}
          helperText={translate("settings.order_manual_processing.description")}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <NumberInput
          source="order_checkout_window_minutes"
          label={translate("settings.order_checkout_window_minutes.name")}
          helperText={translate("settings.order_checkout_window_minutes.description")}
          min={1}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6">{translate("settings.headlines.availability_checks")}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
          {translate("settings.order_validation_levels.help")}
        </Typography>
      </Grid>
      {AVAILABILITY_KEYS.map((key) => (
        <Grid item xs={12} md={4} key={key}>
          <SelectInput
            source={key}
            label={translate(`settings.${key}.name`)}
            helperText={translate(`settings.${key}.description`)}
            choices={VALIDATION_LEVELS.map((level) => ({
              id: level,
              name: translate(`settings.order_validation_levels.${level}`),
            }))}
            // Settings saved before the three levels hold a boolean
            format={(value: unknown) => (value === true ? "everywhere" : value === false ? "off" : value)}
            validate={required()}
          />
        </Grid>
      ))}
      <Grid item xs={12}>
        <BooleanInput
          source="order_validation_billing_address"
          label={translate("settings.order_validation_billing_address.name")}
          helperText={translate("settings.order_validation_billing_address.description")}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6">{translate("settings.headlines.invoicing")}</Typography>
      </Grid>
      <Grid item xs={12}>
        <BooleanInput
          source="invoice_auto_accounting_transfer"
          label={translate("settings.invoice_auto_accounting_transfer.name")}
          helperText={translate("settings.invoice_auto_accounting_transfer.description")}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <NumberInput
          source="invoice_payment_term_days"
          label={translate("settings.invoice_payment_term_days.name")}
          helperText={translate("settings.invoice_payment_term_days.description")}
          min={0}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6">{translate("settings.headlines.debit_number_ranges")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {translate("settings.debit_number_ranges.help")}
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <NumberInput
          source="debit_number_start_index"
          label={translate("settings.debit_number_start_index.name")}
          helperText={translate("settings.debit_number_start_index.description")}
          min={1}
          validate={required()}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <NumberInput
          source="debit_number_end_index"
          label={translate("settings.debit_number_end_index.name")}
          helperText={translate("settings.debit_number_end_index.description")}
          min={1}
          validate={validateEnd("debit_number_start_index")}
        />
      </Grid>
      <Grid item xs={12} md={4} />
      <Grid item xs={12} md={4}>
        <NumberInput
          source="organization_debit_number_start_index"
          label={translate("settings.organization_debit_number_start_index.name")}
          helperText={translate("settings.organization_debit_number_start_index.description")}
          min={1}
          validate={validateOrganizationStart}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <NumberInput
          source="organization_debit_number_end_index"
          label={translate("settings.organization_debit_number_end_index.name")}
          helperText={translate("settings.organization_debit_number_end_index.description")}
          min={1}
          validate={validateEnd("organization_debit_number_start_index")}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6">{translate("settings.headlines.data_deletion")}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <NumberInput
          source="order_unverified_user_retention_days"
          label={translate("settings.order_unverified_user_retention_days.name")}
          helperText={translate("settings.order_unverified_user_retention_days.description")}
          min={0}
        />
      </Grid>
    </>
  );
}
